import {
  Check,
  ChevronsUpDown,
  Languages,
  LogOut,
  Monitor,
  Moon,
  Palette,
  Sun,
  SunMoon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useChangeTheme } from "@/hooks/use-change-theme";
import { useChangeLanguage } from "@/hooks/use-change-language";
import type { AppLanguage } from "@/i18n";
import { UI_THEMES, type UiTheme } from "@/lib/ui-theme";

export interface NavUserInfo {
  name: string;
  email: string;
  avatar?: string;
}

export function userInitials(name: string | undefined): string {
  return (
    name
      ?.split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .join("")
      .slice(0, 3) || "CN"
  );
}

/**
 * The body of the account menu (Profile, Appearance, Language, Sign out),
 * shared by the sidebar footer row and the desktop top bar. Appearance holds
 * both theme axes: light/dark/system and Classic/Lilac (GRA-142 later moves
 * the choice to the user profile; for now it is per browser).
 */
export function NavUserMenuContent({
  user,
  onSignOut,
}: {
  user: NavUserInfo;
  onSignOut: () => void;
}) {
  const { theme, changeTheme, uiTheme, changeUiTheme } = useChangeTheme();
  const { language, changeLanguage } = useChangeLanguage();
  const { t } = useTranslation("common");
  const initials = userInitials(user.name);

  const modeOptions: {
    value: "light" | "dark" | "system";
    label: string;
    icon: typeof Sun;
  }[] = [
    { value: "light", label: t("theme.light"), icon: Sun },
    { value: "dark", label: t("theme.dark"), icon: Moon },
    { value: "system", label: t("theme.system"), icon: Monitor },
  ];

  const uiThemeOptions: { value: UiTheme; label: string }[] = UI_THEMES.map(
    (value) => ({ value, label: t(`theme.ui.${value}`) }),
  );

  const languageOptions: { value: AppLanguage; label: string }[] = [
    { value: "sv", label: t("language.sv") },
    { value: "en", label: t("language.en") },
  ];

  return (
    <>
      <DropdownMenuLabel className="p-0 font-normal">
        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={user.avatar} alt={user.name} />
            <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{user.name}</span>
            <span className="truncate text-xs">{user.email}</span>
          </div>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link to="/profile">{t("profile")}</Link>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <SunMoon />
            {t("appearance")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {modeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = theme === option.value;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => changeTheme(option.value)}
                >
                  <Icon />
                  {option.label}
                  {isActive && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            {uiThemeOptions.map((option) => {
              const isActive = uiTheme === option.value;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => changeUiTheme(option.value)}
                  data-testid={`ui-theme-${option.value}`}
                >
                  <Palette />
                  {option.label}
                  {isActive && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages />
            {t("language.label")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {languageOptions.map((option) => {
              const isActive = language === option.value;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => void changeLanguage(option.value)}
                >
                  {option.label}
                  {isActive && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => onSignOut()}>
        <LogOut />
        {t("signOut")}
      </DropdownMenuItem>
    </>
  );
}

/** The sidebar footer account row (phones and the collapsed rail). */
export function NavUser({
  user,
  onSignOut,
}: {
  user: NavUserInfo;
  onSignOut: () => void;
}) {
  const { isMobile } = useSidebar();
  const initials = userInitials(user.name);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <NavUserMenuContent user={user} onSignOut={onSignOut} />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
